import React, { useEffect, useState } from "react";
import { Container, Row, Col, Card, CardBody, CardHeader, Button, Badge, Spinner, Table } from "reactstrap";
import { useParams, useNavigate, Link } from "react-router-dom";
import { get } from "../../helpers/api_helper";
import Breadcrumbs from "../../components/Common/Breadcrumb";

const PaymentDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [payment, setPayment] = useState(null);

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        const response = await get(`/admin/payments/${id}`);
        if (response.success) {
          setPayment(response.data);
        }
      } catch (error) {
        console.error("Eroare detalii plată:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchDetails();
  }, [id]);

  if (loading) return <div className="text-center p-5"><Spinner color="primary" /></div>;
  if (!payment) return <div className="text-center p-5">Plata nu a fost găsită.</div>;

  const billing = payment.billing_details || {};

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Plăți" breadcrumbItem="Detalii Tranzacție" />

          {/* BACK BUTTON */}
          <Row className="mb-3">
            <Col>
              <Button color="light" onClick={() => navigate(-1)} className="btn-label">
                <i className="uil uil-arrow-left label-icon"></i> Înapoi la listă
              </Button>
            </Col>
          </Row>

          <Row>
            {/* COLOANA STÂNGA: Informații Generale & Anunț */}
            <Col lg={8}>
              <Card>
                <CardHeader className="bg-transparent border-bottom">
                  <div className="d-flex align-items-center">
                    <h5 className="card-title flex-grow-1 mb-0">Tranzacția #{payment.transaction_id}</h5>
                    <Badge color={payment.status === 'paid' ? 'success' : 'warning'} className="font-size-12">
                      {payment.status.toUpperCase()}
                    </Badge>
                  </div>
                </CardHeader>
                <CardBody>
                  <Row>
                    <Col sm={6}>
                      <p className="text-muted mb-2">Detalii Anunț</p>
                      <h5 className="font-size-15">
                        <Link to={`/edit-anunt/${payment.listing_id}`} className="text-dark">
                          {payment.listing?.title}
                        </Link>
                      </h5>
                      <p className="mb-0">ID Anunț: #{payment.listing_id}</p>
                    </Col>
                    <Col sm={6} className="text-sm-end">
                      <p className="text-muted mb-2">Data Tranzacției</p>
                      <h5 className="font-size-15">{new Date(payment.created_at).toLocaleString('ro-RO')}</h5>
                    </Col>
                  </Row>

                  <hr className="my-4" />

                  {/* TABEL SUME */}
                  <div className="table-responsive">
                    <Table className="table-nowrap table-centered mb-0">
                      <thead>
                        <tr>
                          <th>Descriere Serviciu</th>
                          <th className="text-end">Suma Netă</th>
                          <th className="text-end">TVA</th>
                          <th className="text-end">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>Promovare Anunț Apex</td>
                          <td className="text-end">{payment.amount_net} {payment.currency}</td>
                          <td className="text-end">{payment.amount_vat} {payment.currency}</td>
                          <td className="text-end fw-bold">{payment.amount_total} {payment.currency}</td>
                        </tr>
                      </tbody>
                    </Table>
                  </div>
                </CardBody>
              </Card>

              {/* DETALII EXTERNE (Dacă există) */}
              {payment.external_id && (
                <Card>
                  <CardBody>
                    <h5 className="font-size-15 mb-3">Informații Procesator Plăți</h5>
                    <p className="mb-1">ID Extern: <span className="fw-medium">{payment.external_id}</span></p>
                    <p className="mb-0">Monedă decontare: <span className="fw-medium">{payment.currency}</span></p>
                  </CardBody>
                </Card>
              )}
            </Col>

            {/* COLOANA DREAPTĂ: Date Facturare */}
            <Col lg={4}>
              <Card>
                <CardHeader className="bg-primary text-white">
                  <h5 className="card-title mb-0 text-white">
                    <i className="uil uil-invoice me-2"></i> Date Facturare
                  </h5>
                </CardHeader>
                <CardBody>
                  <div className="mb-4">
                    <p className="text-muted mb-1">Tip Client</p>
                    <Badge color="info" className="font-size-11">
                      {payment.billing_type === 'business' ? 'PERSOANĂ JURIDICĂ (FIRMĂ)' : 'PERSOANĂ FIZICĂ'}
                    </Badge>
                  </div>

                  <div className="mb-4">
                    <p className="text-muted mb-1">Nume Complet / Reprezentant</p>
                    <h5 className="font-size-14">{billing.name}</h5>
                  </div>

                  {payment.billing_type === 'business' && (
                    <>
                      <div className="mb-4">
                        <p className="text-muted mb-1">Denumire Companie</p>
                        <h5 className="font-size-14">{billing.company_name}</h5>
                      </div>
                      <Row>
                        <Col md={6}>
                          <div className="mb-4">
                            <p className="text-muted mb-1">C.U.I. / C.I.F.</p>
                            <h5 className="font-size-14">{billing.company_cif}</h5>
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="mb-4">
                            <p className="text-muted mb-1">Nr. Reg. Com.</p>
                            <h5 className="font-size-14">{billing.company_reg}</h5>
                          </div>
                        </Col>
                      </Row>
                    </>
                  )}

                  <div className="mb-0">
                    <p className="text-muted mb-1">Adresă Sediu / Domiciliu</p>
                    <h5 className="font-size-14" style={{ lineHeight: '1.5' }}>{billing.address}</h5>
                  </div>
                </CardBody>
              </Card>

              <Card>
                <CardBody>
                  <Button color="success" className="w-100 mb-2">
                    <i className="uil uil-print me-1"></i> Descarcă Factură (PDF)
                  </Button>
   
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default PaymentDetails;